import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cognitoAuth } from '@/services/auth/cognito';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await cognitoAuth.signIn(email, password);

      if (result.requiresMFA) {
        setShowMfa(true);
        setIsLoading(false);
        return;
      }

      if (!result.success) {
        setError(result.error || 'Login failed');
        setIsLoading(false);
        return;
      }

      await handleSuccessfulAuth();
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await cognitoAuth.confirmMFA(mfaCode);

      if (!result.success) {
        setError(result.error || 'Invalid MFA code');
        setIsLoading(false);
        return;
      }

      await handleSuccessfulAuth();
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessfulAuth = async () => {
    const userInfo = await cognitoAuth.getUserInfo();
    const token = await cognitoAuth.getIdToken();

    if (userInfo && token) {
      setUser({
        id: userInfo.userId,
        cognitoUserId: userInfo.userId,
        tenantId: userInfo.tenantId || '',
        email: userInfo.email,
        firstName: userInfo.givenName || null,
        lastName: userInfo.familyName || null,
        role: (userInfo.role as any) || 'user',
        status: 'active',
        permissions: {},
        lastLoginAt: new Date().toISOString(),
        createdAt: '',
        updatedAt: '',
        createdBy: null,
      });
      setToken(token);
      navigate('/'); // Navigate to dashboard (relative to /panel basename)
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-col justify-between bg-zinc-900 p-10 text-white">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-xl font-bold">
            R
          </div>
          <span className="text-xl font-semibold">Roni Admin</span>
        </div>

        <div className="space-y-6">
          <blockquote className="space-y-2">
            <p className="text-lg">
              "The admin panel has streamlined our entire organization management.
              Managing users, files, and reports has never been easier."
            </p>
            <footer className="text-sm text-zinc-400">Platform Administrator</footer>
          </blockquote>
        </div>

        <div className="text-sm text-zinc-400">
          &copy; {new Date().getFullYear()} Roni AI. All rights reserved.
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex items-center justify-center p-8">
        <div className="mx-auto w-full max-w-[350px] space-y-6">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-xl font-bold text-primary-foreground">
              R
            </div>
            <span className="text-xl font-semibold">Roni Admin</span>
          </div>

          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              {showMfa ? 'Two-Factor Authentication' : 'Welcome back'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {showMfa
                ? 'Enter the verification code from your authenticator app'
                : 'Enter your credentials to access the admin panel'
              }
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {!showMfa ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 text-xs font-normal h-auto"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot password?
                  </Button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-11 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-11 w-11 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                />
                <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                  Remember me for 30 days
                </Label>
              </div>

              <Button type="submit" className="w-full h-11" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign in
              </Button>
            </form>
          ) : (
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <div className="flex justify-center mb-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <ShieldCheck className="h-8 w-8 text-primary" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mfaCode">Verification Code</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  required
                  disabled={isLoading}
                  className="h-14 text-center text-2xl tracking-[0.5em] font-mono"
                  autoComplete="one-time-code"
                />
              </div>

              <Button type="submit" className="w-full h-11" disabled={isLoading || mfaCode.length < 6}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShowMfa(false);
                  setMfaCode('');
                  setError('');
                }}
              >
                Back to login
              </Button>
            </form>
          )}

          <p className="text-center text-sm text-muted-foreground">
            This is a secure admin area.{' '}
            <a href="/" className="underline underline-offset-4 hover:text-primary">
              Return to main site
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
